(function () {
  const DESKTOP_SCROLL_QUERY = window.matchMedia("(min-width: 990px)");

  function getScrollContainer() {
    if (DESKTOP_SCROLL_QUERY.matches) {
      return document.querySelector(".page-wrapper") ?? document.documentElement;
    }

    return document.scrollingElement ?? document.documentElement;
  }

  function getScrollEventTarget() {
    if (DESKTOP_SCROLL_QUERY.matches) {
      return document.querySelector(".page-wrapper") ?? document;
    }

    return document;
  }

  function getScrollTop() {
    const container = getScrollContainer();
    return container.scrollTop ?? window.scrollY ?? 0;
  }

  function escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }

  function setupHeaderHoverState(header) {
    if (!header || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    let pointerInside = false;

    function syncHoverState(isInside) {
      if (pointerInside === isInside) return;
      pointerInside = isInside;
      header.classList.toggle("is-hovered", isInside);
    }

    header.addEventListener("pointerenter", () => syncHoverState(true));
    header.addEventListener("pointerleave", () => syncHoverState(false));
  }

  function setupPersistentHeader(header) {
    if (!header) return;

    let scrollTarget = null;

    function syncHeaderState() {
      const stickyActive = header.getAttribute("data-sticky-state") === "active";
      const hero = document.querySelector(
        ".hero, .cb-collection-hero, .gift-guide-hero, .page-hero, #MainContent > .shopify-section:first-child",
      );
      const threshold = hero ? Math.min(24, Math.max(8, Math.round(hero.offsetHeight * 0.08))) : 12;
      const scrolled = stickyActive || getScrollTop() > threshold;

      header.classList.toggle("is-scrolled", scrolled);
    }

    function bindScrollListener() {
      if (scrollTarget) {
        scrollTarget.removeEventListener("scroll", syncHeaderState);
      }

      scrollTarget = getScrollEventTarget();
      scrollTarget.addEventListener("scroll", syncHeaderState, { passive: true });
      syncHeaderState();
    }

    bindScrollListener();
    DESKTOP_SCROLL_QUERY.addEventListener("change", bindScrollListener);
    window.addEventListener("resize", syncHeaderState);

    if (typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver(syncHeaderState);
      observer.observe(header, { attributes: true, attributeFilter: ["data-sticky-state"] });
    }
  }

  function isNavigableMenuLink(url) {
    if (!url) return false;
    const normalized = url.trim();
    return Boolean(normalized) && normalized !== "#" && normalized !== "#top" && !normalized.startsWith("javascript:");
  }

  function initChrisbellaHeader() {
    const header = document.querySelector("#header-component");
    if (!header) return;

    setupHeaderHoverState(header);
    setupPersistentHeader(header);
  }

  function setupMegaMenu() {
    const header = document.querySelector("#header-component");
    if (!header) return;

    const triggers = [...document.querySelectorAll("#header-component [data-menu]")];
    const plainDesktopLinks = [...document.querySelectorAll("#header-component .desktop-nav > a")];
    const menu = document.querySelector("#header-component #megaMenu");
    const columnsRoot = document.querySelector("#header-component #megaColumns");
    const promosRoot = document.querySelector("#header-component #megaPromos");

    if (!triggers.length || !menu || !columnsRoot || !promosRoot) return;

    const configuredMenus = Object.fromEntries(
      [...document.querySelectorAll("#header-component [data-shopify-menu-definition]")].map((definition) => [
        definition.dataset.menuKey,
        {
          columns: [...definition.querySelectorAll("[data-shopify-menu-column]")].map((column) => ({
            title: column.dataset.menuColumnTitle || "",
            links: [...column.querySelectorAll("[data-shopify-menu-link]")]
              .map((link) => ({
                title: link.textContent.trim(),
                url: link.getAttribute("href") || "/",
              }))
              .filter((item) => isNavigableMenuLink(item.url) && item.title),
          })),
          promos: [],
        },
      ]),
    );

    let closeTimer;
    let pointerTriggeredFocus = false;

    function renderMegaMenu(menuKey) {
      const data = configuredMenus[menuKey];
      if (!data) return false;

      const visibleColumns = data.columns.filter((column) => column.links.length > 0);
      if (!visibleColumns.length) return false;

      menu.classList.add("is-links-only");
      columnsRoot.style.setProperty("--mega-column-count", String(Math.max(visibleColumns.length, 1)));
      promosRoot.hidden = true;

      columnsRoot.innerHTML = visibleColumns
        .map(
          (column) => `
          <div class="mega-column">
            <h3>${escapeHtml(column.title)}</h3>
            ${column.links
              .map(
                (item) =>
                  `<a href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>`,
              )
              .join("")}
          </div>
        `,
        )
        .join("");

      promosRoot.innerHTML = "";
      return true;
    }

    function openMenu(menuKey) {
      window.clearTimeout(closeTimer);
      if (!renderMegaMenu(menuKey)) return;
      header.classList.add("is-menu-open");
      menu.classList.add("is-open");
      menu.setAttribute("aria-hidden", "false");
      triggers.forEach((trigger) => {
        trigger.setAttribute("aria-expanded", String(trigger.dataset.menu === menuKey));
      });
    }

    function closeMenu() {
      header.classList.remove("is-menu-open");
      menu.classList.remove("is-open");
      menu.setAttribute("aria-hidden", "true");
      triggers.forEach((trigger) => trigger.setAttribute("aria-expanded", "false"));
    }

    function closeMenuImmediately() {
      window.clearTimeout(closeTimer);
      closeMenu();
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener("pointerdown", () => {
        pointerTriggeredFocus = true;
      });
      trigger.addEventListener("mouseenter", () => openMenu(trigger.dataset.menu));
      trigger.addEventListener("focus", () => {
        if (!pointerTriggeredFocus) openMenu(trigger.dataset.menu);
      });
      trigger.addEventListener("click", () => {
        if (trigger.getAttribute("aria-expanded") === "true") {
          closeMenu();
        } else {
          openMenu(trigger.dataset.menu);
        }
        pointerTriggeredFocus = false;
      });
    });

    plainDesktopLinks.forEach((link) => {
      link.addEventListener("mouseenter", closeMenuImmediately);
      link.addEventListener("focus", closeMenuImmediately);
    });

    const logoLink = header.querySelector(".header-logo");
    if (logoLink) {
      logoLink.addEventListener("click", closeMenuImmediately);
    }

    header.addEventListener("mouseleave", () => {
      closeTimer = window.setTimeout(closeMenu, 180);
    });

    header.addEventListener("focusout", () => {
      window.setTimeout(() => {
        if (!header.contains(document.activeElement)) closeMenu();
      }, 0);
    });

    menu.addEventListener("mouseenter", () => window.clearTimeout(closeTimer));

    document.addEventListener("click", (event) => {
      if (!header.contains(event.target)) closeMenu();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeMenu();
    });
  }

  function init() {
    initChrisbellaHeader();
    setupMegaMenu();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
